import { Head, router } from "@inertiajs/react"
import { Link as LinkIcon, Share2, Trophy, Users } from "lucide-react"
import { useEffect, useState } from "react"

import { AuthButton } from "@/components/auth-button"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import { useAuth } from "@/hooks/use-auth"
import AppLayout from "@/layouts/app-layout"

interface Campaign {
  id: number
  title: string
  subtitle?: string
  slug: string
  description?: string
  prize_image?: string
  prize_value?: number
  status: "draft" | "active" | "ended" | "drawn"
  start_date?: string
  end_date?: string
  draw_date?: string
  base_entries: number
  entries_per_referral: number
  max_entries_per_user?: number
  max_referral_levels: number
  primary_color?: string
  direct_host_url?: string
}

interface Participant {
  id: number
  referral_code: string
  total_entries_cached: number
  base_entries: number
  referral_entries: number
  bonus_entries: number
}

interface Props {
  campaign: Campaign
  participant?: Participant
  referral_param?: string
}

export default function ViralCampaignShow({
  campaign,
  participant,
  referral_param,
}: Props) {
  const isActive = campaign.status === "active"
  const { isAuthenticated } = useAuth()
  const [toastMsg, setToastMsg] = useState<string | null>(null)
  useEffect(() => {
    if (!toastMsg) return
    const t = setTimeout(() => setToastMsg(null), 2000)
    return () => clearTimeout(t)
  }, [toastMsg])

  const baseShareUrl = campaign.direct_host_url
    ? `${window.location.origin}${campaign.direct_host_url}`
    : `${window.location.origin}/sorteo/${campaign.slug}`

  const referralLink = participant
    ? `${baseShareUrl}?ref=${participant.referral_code}`
    : baseShareUrl

  const handleParticipate = () => {
    void router.post(
      `/viral/campaigns/${campaign.id}/participants`,
      {
        ref: referral_param,
      },
      { preserveScroll: true },
    )
  }

  const handleShare = (platform: "whatsapp" | "copy_link") => {
    if (!participant) return

    if (platform === "whatsapp") {
      const message = encodeURIComponent(
        `¡Estoy participando en un sorteo en CurrAR! Registrate y ayudame a sumar más chances en este sorteo \n${referralLink}`,
      )
      window.open(`https://wa.me/?text=${message}`, "_blank")
      setToastMsg("Abriendo WhatsApp… Gracias por compartir")
    } else if (platform === "copy_link") {
      void navigator.clipboard?.writeText(referralLink)
      setToastMsg("Link copiado al portapapeles")
    }

    void router.post(
      `/viral/campaigns/${campaign.id}/shares`,
      {
        platform,
        message_template_used: "default_1",
      },
      { preserveScroll: true },
    )
  }

  return (
    <AppLayout>
      <Head title={campaign.title} />
      <div className="mx-auto max-w-2xl space-y-6 p-4">
        {toastMsg && (
          <div className="fixed top-4 left-1/2 z-50 -translate-x-1/2 rounded-md bg-black/80 px-3 py-2 text-sm text-white shadow">
            {toastMsg}
          </div>
        )}
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl font-bold">
              {campaign.title}
            </CardTitle>
            {campaign.subtitle && (
              <div className="text-muted-foreground">{campaign.subtitle}</div>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {campaign.prize_image && (
              <div className="bg-muted relative aspect-video overflow-hidden rounded-lg">
                <img
                  src={campaign.prize_image}
                  alt={campaign.title}
                  className="h-full w-full object-cover"
                />
              </div>
            )}

            <div className="flex items-center gap-2 text-sm">
              <Trophy className="h-4 w-4 text-yellow-500" />
              <span>Entradas base: {campaign.base_entries}</span>
              <span>•</span>
              <span>Por referido: +{campaign.entries_per_referral}</span>
            </div>

            <Separator />

            <div className="grid grid-cols-1 gap-3">
              {!participant ? (
                isAuthenticated ? (
                  <Button
                    size="lg"
                    disabled={!isActive}
                    onClick={handleParticipate}
                  >
                    Participar ahora
                  </Button>
                ) : (
                  <div className="space-y-2">
                    <div className="text-muted-foreground text-sm">
                      Te han invitado a participar. Registrate para sumar tus
                      chances.
                    </div>
                    <AuthButton authAction="register">
                      <Button size="lg" disabled={!isActive}>
                        Registrarme y participar
                      </Button>
                    </AuthButton>
                  </div>
                )
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="text-muted-foreground flex items-center gap-2 text-sm">
                      <Users className="h-4 w-4" />
                      <span>Tus entradas</span>
                    </div>
                    <div className="text-xl font-bold">
                      {participant.total_entries_cached}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant="outline"
                      onClick={() => handleShare("whatsapp")}
                    >
                      <Share2 className="mr-2 h-4 w-4" /> Compartir WhatsApp
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleShare("copy_link")}
                    >
                      <LinkIcon className="mr-2 h-4 w-4" /> Copiar link
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
